import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DeleteOutlined, EditOutlined, CheckCircleTwoTone, CloseCircleTwoTone } from '@ant-design/icons';
import { Table, Modal, Tooltip, Divider } from 'antd';
import _ from 'lodash';
import qs from 'qs';
import TextTruncate from 'react-text-truncate';
import HtmlParser from 'react-html-parser';
import PageTitle from '../../components/PageTitle';
import TableActionBar from '../../components/TableActionBar';
import { checkPermission } from '../../utils/permission';
import { cleanObject, stripHtml, isValidDate } from '../../utils/helpers';
import ScheduleAreaForm from './ScheduleAreaForm';
import {
    fetchScheduleAreas,
    fetchScheduleArea,
    removeScheduleAreas
} from '../../redux/actions/scheduleAreaActions';

const routes = [
    { path: '/', breadcrumbName: 'Trang chủ' },
    { path: '/schedule-areas', breadcrumbName: 'Khu vực làm việc' }
];

class ScheduleAreaList extends Component {
    state = {
        isLoading: false,
        selectedRowKeys: [],
        isOpenForm: false,
        currentItem: null
    };

    componentDidMount() {
        this.loadData();
    }

    componentDidUpdate(previousProps) {
        if (previousProps.location.search !== this.props.location.search) {
            this.loadData();
        }
    }

    loadData = () => {
        const query = qs.parse(this.props.location.search.slice(1));
        this.setState({ isLoading: true });
        return this.props.fetchScheduleAreas(query)
            .catch(() => null)
            .finally(() => this.setState({ isLoading: false }));
    };

    changeFilter = (name, value) => {
        const query = cleanObject({
            ...qs.parse(this.props.location.search.slice(1)),
            [name]: value,
            page: undefined
        });
        this.props.history.push({ pathname: this.props.location.pathname, search: qs.stringify(query) });
    };

    changeTable = (pagination, sorter) => {
        const query = { ...qs.parse(this.props.location.search.slice(1)) };
        if (!_.isEmpty(pagination)) {
            query.page = pagination.current;
            query.per_page = pagination.pageSize;
        }
        if (sorter.order) {
            query.sort_field = sorter.field;
            query.sort_type = sorter.order === 'ascend' ? 'ASC' : 'DESC';
        } else {
            delete query.sort_field;
            delete query.sort_type;
        }
        this.props.history.push({ pathname: this.props.location.pathname, search: qs.stringify(query) });
    };

    openForm = (currentItem = null) => this.setState({ isOpenForm: true, currentItem });
    closeForm = () => this.setState({ isOpenForm: false, currentItem: null });

    edit = id => this.props.fetchScheduleArea(id).then(this.openForm).catch(() => null);

    remove = ids => this.props.removeScheduleAreas(ids).then(() => {
        this.setState({ selectedRowKeys: [] });
        this.loadData();
    }).catch(() => null);

    confirmRemove = ids => Modal.confirm({
        title: 'Cảnh báo',
        content: 'Bạn chắc chắn muốn xoá những khu vực đã chọn?',
        okText: 'OK',
        cancelText: 'Huỷ',
        onOk: () => this.remove(ids)
    });

    render() {
        const { items, pagination } = this.props;
        const { isLoading, selectedRowKeys, isOpenForm, currentItem } = this.state;
        const query = qs.parse(this.props.location.search.slice(1));
        const columns = [
            { title: 'ID', dataIndex: 'id', key: 'id', width: 60, sorter: true },
            {
                title: '#', width: 80, align: 'center',
                render: (_, record) => (
                    <div>
                        {checkPermission('schedule_areas', 'update') ? (
                            <>
                                <EditOutlined className="color-primary" onClick={() => this.edit(record.id)} />
                                <Divider type="vertical" />
                            </>
                        ) : null}
                        {checkPermission('schedule_areas', 'remove') ? (
                            <DeleteOutlined style={{ color: 'red' }} onClick={() => this.confirmRemove([record.id])} />
                        ) : null}
                    </div>
                )
            },
            { title: 'Tên khu vực', dataIndex: 'name', key: 'name', sorter: true },
            {
                title: 'Ghi chú', dataIndex: 'note', key: 'note', width: 220,
                render: text => (
                    <Tooltip title={HtmlParser(text || '')} placement="topLeft">
                        <div style={{ wordBreak: 'break-word' }}>
                            <TextTruncate line={1} truncateText="…" text={stripHtml(text || '')} />
                        </div>
                    </Tooltip>
                )
            },
            { title: 'Người tạo', dataIndex: 'created_user', key: 'created_user', width: 180, sorter: true },
            { title: 'Ngày tạo', dataIndex: 'created_at', key: 'created_at', width: 150, sorter: true, render: isValidDate },
            {
                title: 'Trạng thái', dataIndex: 'status', key: 'status', width: 100, align: 'center',
                render: value => Number(value) === 1
                    ? <CheckCircleTwoTone twoToneColor="#52c41a" />
                    : <CloseCircleTwoTone twoToneColor="#e74c3c" />
            }
        ];

        return (
            <div>
                <PageTitle routes={routes} title="Khu vực làm việc" />
                <TableActionBar
                    isShowAddButton={checkPermission('schedule_areas', 'create')}
                    isShowDeleteButton={checkPermission('schedule_areas', 'remove')}
                    onAdd={() => this.openForm()}
                    onDelete={() => this.confirmRemove(selectedRowKeys)}
                    disabled={!selectedRowKeys.length}
                    showFilter={false}
                    onSearch={value => this.changeFilter('keyword', value)}
                    defaultKeyword={query.keyword}
                >
                    {selectedRowKeys.length ? <span>Đang chọn {selectedRowKeys.length} bản ghi</span> : null}
                </TableActionBar>
                <Table
                    rowKey="id"
                    size="small"
                    dataSource={items}
                    columns={columns}
                    loading={isLoading}
                    rowSelection={{ selectedRowKeys, onChange: keys => this.setState({ selectedRowKeys: keys }) }}
                    pagination={{
                        pageSize: pagination.perPage,
                        current: pagination.currentPage,
                        total: pagination.total,
                        showTotal: total => `Tổng ${total} bản ghi`
                    }}
                    onChange={(page, filters, sorter) => this.changeTable(page, sorter)}
                    scroll={{ x: 'max-content' }}
                />
                <ScheduleAreaForm
                    visible={isOpenForm}
                    currentItem={currentItem}
                    reloadData={this.loadData}
                    onCancel={this.closeForm}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    items: state.scheduleAreaReducer.items,
    pagination: state.scheduleAreaReducer.pagination
});

const mapDispatchToProps = dispatch => ({
    fetchScheduleAreas: filters => dispatch(fetchScheduleAreas(filters)),
    fetchScheduleArea: id => dispatch(fetchScheduleArea(id)),
    removeScheduleAreas: ids => dispatch(removeScheduleAreas(ids))
});

export default connect(mapStateToProps, mapDispatchToProps)(ScheduleAreaList);
